import { SelectChangeEvent } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import { usePopupContext } from "../../../contexts/popupProvider";
import { useAppSelector } from "../../../hooks";
import { selectCurrentUser } from "../../authentication/services";
import { selectLang, setLang } from "../../langs/services";
import { FALLBACK_TERMS_CONTENT } from "../data/fallbackTermsOfServices";
import { fetchTermsContent, LanguageCode } from "../services/fetchTermsOfService";
import { ServiceTerms } from "../types/interface";

export const TERMS_STORAGE_KEY_SSOS = "ssos_terms_accepted";
export const TERMS_STORAGE_KEY_AI_AMAZE = "amaze_terms_accepted";

interface TermsAcceptanceData {
    accepted: boolean;
    version: string;
    timestamp: string;
}

const useTermsOfService = () => {
    //const [isTermsAccepted, setIsTermsAccepted] = useState<boolean>(false);
    const [content, setContent] = useState<ServiceTerms | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const { setPopups } = usePopupContext();
    const { i18n } = useTranslation();
    const systemLang = i18n.language as LanguageCode;
    const currentUser = useSelector(selectCurrentUser);
    const isSaleAccount = !!currentUser?.salesAccountId;

    const selectedLang = useAppSelector(selectLang);

    const checkTermsAcceptance = useCallback(() => {
        try {
            const storedData = localStorage.getItem(
                isSaleAccount ? TERMS_STORAGE_KEY_SSOS : TERMS_STORAGE_KEY_AI_AMAZE
            );

            if (storedData) {
                const termsData: TermsAcceptanceData = JSON.parse(storedData);

                // 檢查版本是否匹配，如果版本不同則需要重新同意
                if (termsData.accepted && termsData.version === content?.version) {
                    setPopups({ terms_of_service: false });
                } else {
                    // 版本不匹配或未同意，需要顯示條款
                    setPopups({ terms_of_service: true });
                }
            } else {
                // 沒有儲存的資料，需要顯示條款

                setPopups({ terms_of_service: true });
            }
        } finally {
            setIsLoading(false);
        }
    }, [content?.version, setPopups, isSaleAccount]);

    const handleConfirm = () => {
        if (content?.version) {
            const termsData: TermsAcceptanceData = {
                accepted: true,
                version: content.version,
                timestamp: new Date().toISOString(),
            };
            localStorage.setItem(
                isSaleAccount ? TERMS_STORAGE_KEY_SSOS : TERMS_STORAGE_KEY_AI_AMAZE,
                JSON.stringify(termsData)
            );
            setPopups({ terms_of_service: false });
        }
    };

    const dispatch = useDispatch();

    const handleLangChange = (event: SelectChangeEvent) => {
        dispatch(setLang(event.target.value));
    };

    useEffect(() => {
        dispatch(setLang(localStorage.getItem("lang") || systemLang));
    }, [dispatch, systemLang]);
    useEffect(() => {
        const loadTermsContent = async () => {
            try {
                setIsLoading(true);
                const response = await fetchTermsContent();
                const termsContents = response?.data?.[systemLang]; // 從回傳的物件中取出 data 陣列
                // 假設 termsContents 是一個陣列，我們需要找到 alias 為 "ai-amaze" 的那一個

                const aiAmazeContent = termsContents || FALLBACK_TERMS_CONTENT;

                setContent(aiAmazeContent);
            } finally {
                setIsLoading(false);
            }
        };

        loadTermsContent();
    }, [systemLang]);

    useEffect(() => {
        if (content) {
            checkTermsAcceptance();
        }
    }, [content, checkTermsAcceptance]);

    return { content, isLoading, handleConfirm, handleLangChange, selectedLang };
};
export default useTermsOfService;
